import {StrtInitiative} from '../models/plan/StrtInitiative';
import {StrtPriority} from '../models/plan/StrtPriority';
import {StrtObjective} from '../models/plan/StrtObjective';
import {StrtKeyMeasure} from '../models/plan/StrtKeyMeasure';
import {StrtOutcome} from '../models/plan/StrtOutcome';
import {SelectItem} from 'primeng/primeng';
import {InitiativeAndPriority} from '../../strategies/strategy-map-download/initiative-and-priority';
import {StrtActivity} from '../models/plan/StrtActivity';
import {StrtAssumption} from '../models/plan/StrtAssumption';
import {StrtInfluentialFactor} from '../models/plan/StrtInfluentialFactor';

export class DataUtils {
  static create(priorities: StrtPriority[]) {
    return new DataUtils(priorities);
  }

  static flatten(arr) {
    return arr.reduce((flat, toFlatten) => {
      if (!flat) {
        console.log(`Flatten Function Missing Required Value: `, flat);
      }
      return flat.concat(Array.isArray(toFlatten) ? this.flatten(toFlatten) : toFlatten);
    }, []);
  }


  /**
   * Will return an array of objective objects attached to the given priority
   * @param {IPriority} priority
   * @param {number} maxCount
   * @returns {IObjective[]}
   */
  public static topObjectives(priority: StrtPriority, maxCount: number): StrtObjective[] {
    const subsetObjectives = [];
    if (priority.objectives && priority.objectives.length > 0) {
      const delta = priority.objectives.length - subsetObjectives.length;
      subsetObjectives.push(... priority.objectives.slice(0, Math.min(maxCount, delta < 0 ? 0 : delta)));
    }
    return subsetObjectives;
  }


  /**
   * Will return an array of initiative objects attached to any objectives attached
   *  to the given priority
   * @param {IPriority} priority
   * @param {number} maxCount
   * @returns {IInitiative[]}
   */
  public static topInitiatives(priority: StrtPriority, maxCount: number): StrtInitiative[] {
    const subsetInitiatives = [];
    let breakOut = false;
    priority.objectives.some((obj: StrtObjective) => {
      if (obj.initiatives && obj.initiatives.length > 0) {
        const sliceLength = Math.min(maxCount - subsetInitiatives.length, obj.initiatives.length);
        subsetInitiatives.push(... obj.initiatives.slice(0, sliceLength));
        if (subsetInitiatives.length >= maxCount) {
          breakOut = true;
        }
      }
      return breakOut;
    });
    return subsetInitiatives;
  }

  /**
   * Will return an array of keyMeasure objects attached to any initiatives attached
   *  to any objectives attached to the given priority
   * @param {IPriority} priority
   * @param {number} maxCount
   * @returns {IKeyMeasure[]}
   */
  public static topKeyMeasures(priority: StrtPriority, maxCount: number): StrtKeyMeasure[] {
    const subsetKeyMeasures = [];
    let breakOut = false;
    priority.objectives.some((obj: StrtObjective) => {
      if (obj.initiatives && obj.initiatives.length > 0) {
        obj.initiatives.some((initiative: StrtInitiative) => {
          if (initiative.keyMeasures && initiative.keyMeasures.length > 0) {
            const sliceLength = Math.min(maxCount - subsetKeyMeasures.length, initiative.keyMeasures.length);
            subsetKeyMeasures.push(... initiative.keyMeasures.slice(0, sliceLength));
            if (subsetKeyMeasures.length >= maxCount) {
              breakOut = true;
            }
          }
          return breakOut;
        });
      }
      return breakOut;
    });
    return subsetKeyMeasures;
  }

  public static reshapePriorities(priorities: StrtPriority[]):  SelectItem[] {
    const obj: SelectItem[] = [];
    priorities.forEach(p => {
      p.objectives.forEach(o => {
        o.initiatives.forEach(i => {
          obj.push({ label: i.description, value: new InitiativeAndPriority(i, p)});
        });
      });
    });
    return obj;
  }

  /*
  potentially replace the the property named `${property}` in the given object with a new corresponding date instance.
  Note: Makes assumptions on string format and timezone
  */
  public static replaceDatePropertyIfPresent(obj: any, property: string) {
    const dateString: string = obj[property];
    if (dateString) {
      const parts = dateString.split('-');
      obj[property] = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
  }


  private constructor(private priorities: StrtPriority[]) {}

  getPriorityById(priorityId: number): StrtPriority {
    return this.priorities.find(it => it.priorityId === priorityId);
  }

  getObjectivePathById(end: number): {objective: StrtObjective, priority: StrtPriority} {
    let objective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => o.objectiveId === end);
      return !!objective;
    });
    return {objective, priority};
  }

  getInitiativePathById(end: number): {initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => i.initiativeId === end);
        return !!initiative;
      });
      return !!objective;
    });
    return {initiative, objective, priority};
  }

  getKeyMeasurePathById(end: number):
    {keyMeasure: StrtKeyMeasure, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let keyMeasure: StrtKeyMeasure;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          keyMeasure = i.keyMeasures.find(k => k.keyMeasureId === end);
          return !!keyMeasure;
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {keyMeasure, initiative, objective, priority};
  }

  getKeyMeasurePathByIdWithInitiativeId(end: number, initiativeId: number):
    {keyMeasure: StrtKeyMeasure, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let keyMeasure: StrtKeyMeasure;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          if (i.initiativeId === initiativeId) {
            keyMeasure = i.keyMeasures.find(k => k.keyMeasureId === end);
            return !!keyMeasure;
          } else {
            return false;
          }
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {keyMeasure, initiative, objective, priority};
  }

  getOutcomePathById(end: number): {outcome: StrtOutcome, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let outcome: StrtOutcome;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          outcome = i.outcomes.find(t => t.outcomeId === end);
          return !!outcome;
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {outcome, initiative, objective, priority};
  }

  getAssumptionPathById(end: number):
    {assumption: StrtAssumption, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let assumption: StrtAssumption;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          assumption = i.assumptions.find(t => t.assumptionId === end);
          return !!assumption;
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {assumption, initiative, objective, priority};
  }

  getInfluentialFactorPathById(end: number):
    {influentialFactor: StrtInfluentialFactor, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let influentialFactor: StrtInfluentialFactor;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          influentialFactor = i.influentialFactors.find(t => t.influentialFactorId === end);
          return !!influentialFactor;
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {influentialFactor, initiative, objective, priority};
  }

  getActivityPathById(end: number): {activity: StrtActivity, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let activity: StrtActivity;
    let initiative: StrtInitiative;
    let objective: StrtObjective;
    const priority = this.priorities.find(p => {
      objective = p.objectives.find(o => {
        initiative = o.initiatives.find(i => {
          activity = i.activities.find(t => t.activityId === end);
          return !!activity;
        });
        return !!initiative;
      });
      return !!objective;
    });
    return {activity, initiative, objective, priority};
  }


  getObjectivePath(end: StrtObjective): {objective: StrtObjective, priority: StrtPriority} {
    const objective = this.getObjectivePathById(end.objectiveId).objective;
    const priority = this.getPriorityById(end.priorityId);
    return {objective, priority};
  }

  getInitiativePath(end: StrtInitiative): {initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let initiative: StrtInitiative;
    this.priorities.find(p => !!p.objectives.find(o => {
      initiative = o.initiatives.find(i => i.initiativeId === end.initiativeId);
      return !!initiative;
    }));
    return Object.assign({initiative}, this.getObjectivePathById(end.objectiveId));
  }

  getKeyMeasurePath(end: StrtKeyMeasure):
    {keyMeasure: StrtKeyMeasure, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let keyMeasure: StrtKeyMeasure;
    this.priorities.find(p => !!p.objectives.find(o => !!o.initiatives.find(i => {
      keyMeasure = i.keyMeasures.find(k => k.keyMeasureId === end.keyMeasureId);
      return !!keyMeasure;
    })));
    return Object.assign({keyMeasure}, this.getInitiativePathById(end.initiativeId));
  }

  getAssumptionPath(end: StrtAssumption):
    {assumption: StrtAssumption, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let assumption: StrtAssumption;
    this.priorities.find(p => !!p.objectives.find(o => !!o.initiatives.find(i => {
      if (!i.assumptions) {
        return false;
      }
      assumption = i.assumptions.find(a => a.assumptionId === end.assumptionId);
      return !!assumption;
    })));
    return Object.assign({assumption}, this.getInitiativePathById(end.initiativeId));
  }

  getInfluentialFactorPath(end: StrtInfluentialFactor):
    {influentialFactor: StrtInfluentialFactor, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let influentialFactor: StrtInfluentialFactor;
    this.priorities.find(p => !!p.objectives.find(o => !!o.initiatives.find(i => {
      if (!i.influentialFactors) {
        return false;
      }
      influentialFactor = i.influentialFactors.find(a => a.influentialFactorId === end.influentialFactorId);
      return !!influentialFactor;
    })));
    return Object.assign({influentialFactor}, this.getInitiativePathById(end.initiativeId));
  }

  getOutcomePath(end: StrtOutcome): {outcome: StrtOutcome, initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority} {
    let outcome: StrtOutcome;
    this.priorities.find(p => !!p.objectives.find(o => !!o.initiatives.find(i => {
      outcome = i.outcomes.find(t => t.outcomeId === end.outcomeId);
      return !!outcome;
    })));
    return Object.assign({outcome}, this.getInitiativePathById(end.initiativeId));
  }

  getActivityPath(end: StrtActivity): {activity: StrtActivity, initiative: StrtInitiative, objective: StrtObjective,
    priority: StrtPriority} {
    let activity: StrtActivity;
    this.priorities.find(p => !!p.objectives.find(o => !!o.initiatives.find(i => {
      activity = i.activities.find(t => t.activityId === end.activityId);
      return !!activity;
    })));
    return Object.assign({activity}, this.getInitiativePathById(end.initiativeId));
  }

  // ==========================================================================

  reconcileObjective(objective: StrtObjective) {
    const curPath = this.getObjectivePathById(objective.objectiveId);
    const newPath = this.getObjectivePath(objective);
    if (curPath.objective) {
      if (curPath.priority.priorityId !== objective.priorityId) {
        curPath.priority.objectives = curPath.priority.objectives.filter(it => it.objectiveId !== curPath.objective.objectiveId);
        if (newPath.priority) {
          newPath.priority.objectives =
            [...newPath.priority.objectives, curPath.objective].sort((a, b) => a.objectiveId - b.objectiveId);
        }
      }
      Object.assign(curPath.objective, objective, {initiatives: curPath.objective.initiatives});
    } else if (newPath.priority) {
      newPath.priority.objectives = [...newPath.priority.objectives, objective].sort((a, b) => a.objectiveId - b.objectiveId);
    }
  }

  reconcileInitiative(initiative: StrtInitiative) {
    const curPath = this.getInitiativePathById(initiative.initiativeId);
    const newPath = this.getInitiativePath(initiative);
    if (curPath.initiative) {
      if (curPath.objective.objectiveId !== initiative.objectiveId) {
        curPath.objective.initiatives = curPath.objective.initiatives.filter(it => it.initiativeId !== curPath.initiative.initiativeId);
        if (newPath.objective) {
          newPath.objective.initiatives =
            [...newPath.objective.initiatives, curPath.initiative].sort((a, b) => a.sort - b.sort);
        }
      }
      // replace initiative properties except for the outcomes and keymeasures
      Object.assign(curPath.initiative, initiative, {outcomes: curPath.initiative.outcomes, keyMeasures: curPath.initiative.keyMeasures});
    } else if (newPath.objective) {
      newPath.objective.initiatives = [...newPath.objective.initiatives, initiative].sort((a, b) => a.sort - b.sort);
    }
  }

  reconcileKeyMeasure(keyMeasure: StrtKeyMeasure) {
    const curPath = this.getKeyMeasurePathById(keyMeasure.keyMeasureId);
    const newPath = this.getKeyMeasurePath(keyMeasure);
    if (curPath.keyMeasure) {
      if (curPath.initiative.initiativeId !== keyMeasure.initiativeId) {
        curPath.initiative.keyMeasures = curPath.initiative.keyMeasures.filter(it => it.keyMeasureId !== curPath.keyMeasure.keyMeasureId);
        if (newPath.initiative) {
          newPath.initiative.keyMeasures =
            [...newPath.initiative.keyMeasures, curPath.keyMeasure].sort((a, b) => a.keyMeasureId - b.keyMeasureId);
        }
      }
      Object.assign(curPath.keyMeasure, keyMeasure);
    } else if (newPath.initiative) {
      newPath.initiative.keyMeasures = [...newPath.initiative.keyMeasures, keyMeasure].sort((a, b) => a.keyMeasureId - b.keyMeasureId);
    }
  }

  addKeyMeasure(newKeyMeasure: StrtKeyMeasure) {
    const newPath = this.getKeyMeasurePath(newKeyMeasure);
    newPath.initiative.keyMeasures = [...newPath.initiative.keyMeasures, newKeyMeasure].sort((a, b) => a.keyMeasureId - b.keyMeasureId);
    if (newKeyMeasure.reused) {
      // update remaining instances of this key measure since reused flag may have changed from false to true
      this.editKeyMeasure(newKeyMeasure);
    }
  }

  addAssumption(newAssumption: StrtAssumption) {
    const newPath = this.getAssumptionPath(newAssumption);
    newPath.initiative.assumptions = [...(newPath.initiative.assumptions || []), newAssumption]
      .sort((a, b) => a.sort - b.sort);
  }

  addAssumptions(newAssumptions: StrtAssumption[]) {
    for (const assumption of newAssumptions) {
      this.addAssumption(assumption);
    }
  }

  addInfluentialFactor(newInfluentialFactor: StrtInfluentialFactor) {
    const newPath = this.getInfluentialFactorPath(newInfluentialFactor);
    newPath.initiative.influentialFactors = [...(newPath.initiative.influentialFactors || []), newInfluentialFactor]
      .sort((a, b) => a.sort - b.sort);
  }

  addInfluentialFactors(newInfluentialFactors: StrtInfluentialFactor[]) {
    for (const influentialFactor of newInfluentialFactors) {
      this.addInfluentialFactor(influentialFactor);
    }
  }

  deleteKeyMeasure(keyMeasure: StrtKeyMeasure, initiativeId: number) {
    const curPath = this.getKeyMeasurePathByIdWithInitiativeId(keyMeasure.keyMeasureId, initiativeId);
    if (curPath.keyMeasure) {
      if (curPath.initiative.initiativeId === keyMeasure.initiativeId) {
        curPath.initiative.keyMeasures = curPath.initiative.keyMeasures.filter(it => it.keyMeasureId !== curPath.keyMeasure.keyMeasureId);
      }
    }
    if (keyMeasure.reused) {
      const findCount = this.findKeyMeasureCount(keyMeasure);
      if (findCount === 1) {
        keyMeasure.reused = false;
        // update remaining instances of this key measure since reused flag would have changed from true to false
        this.editKeyMeasure(keyMeasure);
      } else if (findCount > 1) {
        keyMeasure.reused = true;
        this.editKeyMeasure(keyMeasure);
      }
    }
  }

  findKeyMeasureCount(keyMeasureToFind: StrtKeyMeasure) {
    let count = 0;
    this.priorities.forEach((priority: StrtPriority) => {
      priority.objectives.forEach((objective: StrtObjective) => {
        objective.initiatives.forEach((initiative: StrtInitiative) => {
          const hasMatchingKM = initiative.keyMeasures.find((keyMeasure: StrtKeyMeasure) => {
            return keyMeasure.keyMeasureId === keyMeasureToFind.keyMeasureId;
          });
          if (hasMatchingKM) {
            count++;
          }
        });
      });
    });
    return count;
  }

  deleteKeyMeasureFromAll(keyMeasureToDelete: StrtKeyMeasure) {
    this.priorities.forEach((priority: StrtPriority) => {
      priority.objectives.forEach((objective: StrtObjective) => {
        objective.initiatives.forEach((initiative: StrtInitiative) => {
          const hasMatchingKM = initiative.keyMeasures.find((keyMeasure: StrtKeyMeasure) => {
            return keyMeasure.keyMeasureId === keyMeasureToDelete.keyMeasureId;
          });
          if (hasMatchingKM) {
            initiative.keyMeasures = initiative.keyMeasures.filter((keyMeasure: StrtKeyMeasure) => {
              return keyMeasure.keyMeasureId !== keyMeasureToDelete.keyMeasureId;
            });
          }
        });
      });
    });
  }

  editKeyMeasure(keyMeasure: StrtKeyMeasure) {
    this.priorities.forEach((priority: StrtPriority) => {
      priority.objectives.forEach((objective: StrtObjective) => {
        objective.initiatives.forEach((initiative: StrtInitiative) => {
          const target: StrtKeyMeasure[] = initiative.keyMeasures.filter((keyMeasureX: StrtKeyMeasure) => {
            return keyMeasureX.keyMeasureId === keyMeasure.keyMeasureId;
          });
          if (target && target.length > 0) {
            target[0].tagId = keyMeasure.tagId;
            target[0].measure = keyMeasure.measure;
            target[0].reused = keyMeasure.reused;
          }
        });
      });
    });
  }

  editAssumption(assumption: StrtAssumption) {
    this.priorities.forEach((priority: StrtPriority) => {
      priority.objectives.forEach((objective: StrtObjective) => {
        objective.initiatives.forEach((initiative: StrtInitiative) => {
          const target: StrtAssumption[] = initiative.assumptions.filter((assumptionX: StrtAssumption) => {
            return assumptionX.assumptionId === assumption.assumptionId;
          });
          if (target && target.length > 0) {
            target[0].sort = assumption.sort;
            target[0].assumptionText = assumption.assumptionText;
          }
        });
      });
    });
  }

  reconcileOutcome(outcome: StrtOutcome) {
    const curPath = this.getOutcomePathById(outcome.outcomeId);
    const newPath = this.getOutcomePath(outcome);
    if (curPath.outcome) {
      if (curPath.initiative.initiativeId !== outcome.initiativeId) {
        curPath.initiative.outcomes = curPath.initiative.outcomes.filter(it => it.outcomeId !== curPath.outcome.outcomeId);
        if (newPath.initiative) {
          newPath.initiative.outcomes =
            [...newPath.initiative.outcomes, curPath.outcome].sort((a, b) => a.outcomeId - b.outcomeId);
        }
      }
      Object.assign(curPath.outcome, outcome);
    } else if (newPath.initiative) {
      newPath.initiative.outcomes = [...newPath.initiative.outcomes, outcome].sort((a, b) => a.outcomeId - b.outcomeId);
    }
  }

  reconcileAssumption(assumption: StrtAssumption) {
    const curPath = this.getAssumptionPathById(assumption.assumptionId);
    const newPath = this.getAssumptionPath(assumption);
    if (curPath.assumption) {
      if (curPath.initiative.initiativeId !== assumption.initiativeId) {
        curPath.initiative.assumptions = curPath.initiative.assumptions.filter(it => it.assumptionId !== curPath.assumption.assumptionId);
        if (newPath.initiative) {
          newPath.initiative.assumptions =
            [...newPath.initiative.assumptions, curPath.assumption].sort((a, b) => a.sort - b.sort);
        }
      }
      Object.assign(curPath.assumption, assumption);
    } else if (newPath.initiative) {
      newPath.initiative.assumptions = [...newPath.initiative.assumptions, assumption].sort((a, b) => a.sort - b.sort);
    }
  }

  reconcileInfluentialFactor(influentialFactor: StrtInfluentialFactor) {
    const curPath = this.getInfluentialFactorPathById(influentialFactor.influentialFactorId);
    const newPath = this.getInfluentialFactorPath(influentialFactor);
    if (curPath.influentialFactor) {
      if (curPath.initiative.initiativeId !== influentialFactor.initiativeId) {
        curPath.initiative.influentialFactors = curPath.initiative.influentialFactors
          .filter(it => it.influentialFactorId !== curPath.influentialFactor.influentialFactorId);
        if (newPath.initiative) {
          newPath.initiative.influentialFactors =
            [...newPath.initiative.influentialFactors, curPath.influentialFactor].sort((a, b) => a.sort - b.sort);
        }
      }
      Object.assign(curPath.influentialFactor, influentialFactor);
    } else if (newPath.initiative) {
      newPath.initiative.assumptions = [...newPath.initiative.assumptions, influentialFactor].sort((a, b) => a.sort - b.sort);
    }
  }

  reconcileActivity(activity: StrtActivity) {
    const curPath = this.getActivityPathById(activity.activityId);
    const newPath = this.getActivityPath(activity);
    if (curPath.activity) {
      if (curPath.initiative.initiativeId !== activity.initiativeId) {
        curPath.initiative.activities = curPath.initiative.activities.filter(it => it.activityId !== curPath.activity.activityId);
        if (newPath.initiative) {
          newPath.initiative.activities =
            [...newPath.initiative.activities, curPath.activity].sort((a, b) => a.activityId - b.activityId);
        }
      }
      Object.assign(curPath.activity, activity);
    } else if (newPath.initiative) {
      newPath.initiative.activities = [...newPath.initiative.activities, activity].sort((a, b) => a.activityId - b.activityId);
    }
  }

  reconcileKeyMeasures(keyMeasures: StrtKeyMeasure[]) {
    keyMeasures.forEach(it => this.reconcileKeyMeasure(it));
  }

  reconcileOutcomes(outcomes: StrtOutcome[]) {
    outcomes.forEach(it => this.reconcileOutcome(it));
  }

  addKeyMeasures(keyMeasures: StrtKeyMeasure[]) {
    keyMeasures.forEach(it => this.addKeyMeasure(it));
  }

}
